import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const SeasonFormList = ({ animeData, initialSeasons = [], onSubmit }) => {
  const [seasons, setSeasons] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Para el formulario de temporada
  const [tempId, setTempId] = useState('');
  const [seasonNumber, setSeasonNumber] = useState(1);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [poster, setPoster] = useState('');
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (initialSeasons && initialSeasons.length > 0) {
      setSeasons(initialSeasons);
    }
  }, [initialSeasons]);

  const addNewSeason = () => {
    const newSeasonId = uuidv4();
    const newSeason = {
      tempId: newSeasonId,
      seasonNumber: seasons.length + 1,
      title: `Temporada ${seasons.length + 1}`,
      description: '',
      poster: '',
      releaseDate: new Date().toISOString().split('T')[0],
      episodeCount: 0,
      episodes: []
    };
    
    setSeasons([...seasons, newSeason]);
    setEditingIndex(seasons.length);
    resetForm(newSeason);
  };

  const resetForm = (season = null) => {
    if (season) {
      setTempId(season.tempId || '');
      setSeasonNumber(season.seasonNumber || 1);
      setTitle(season.title || '');
      setDescription(season.description || '');
      setPoster(season.poster || '');
      setReleaseDate(season.releaseDate || new Date().toISOString().split('T')[0]);
    } else {
      setTempId('');
      setSeasonNumber(seasons.length + 1);
      setTitle(`Temporada ${seasons.length + 1}`);
      setDescription('');
      setPoster('');
      setReleaseDate(new Date().toISOString().split('T')[0]);
    }
  };

  const editSeason = (index) => {
    const season = seasons[index];
    setEditingIndex(index);
    resetForm(season);
  };

  const deleteSeason = (index) => {
    const updatedSeasons = [...seasons];
    updatedSeasons.splice(index, 1);
    
    // Renumerar temporadas
    const renumberedSeasons = updatedSeasons.map((season, idx) => ({
      ...season,
      seasonNumber: idx + 1,
      title: season.title === `Temporada ${season.seasonNumber}` ? `Temporada ${idx + 1}` : season.title
    }));
    
    setSeasons(renumberedSeasons);
    setEditingIndex(null);
    resetForm();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const updatedSeasons = [...seasons];
    
    const seasonData = {
      tempId: tempId || uuidv4(),
      seasonNumber,
      title,
      description,
      poster,
      releaseDate,
      episodeCount: 0,
      episodes: []
    };
    
    if (editingIndex !== null && editingIndex < seasons.length) {
      // Actualizar temporada existente
      updatedSeasons[editingIndex] = {
        ...seasonData,
        episodes: updatedSeasons[editingIndex].episodes || [],
        episodeCount: updatedSeasons[editingIndex].episodes?.length || 0
      };
    } else {
      // Añadir nueva temporada
      updatedSeasons.push(seasonData);
    }
    
    setSeasons(updatedSeasons);
    setEditingIndex(null);
    resetForm();
  };

  const handleComplete = () => {
    onSubmit(seasons);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-white">Temporadas</h3>
        <p className="text-sm text-gray-400">
          Añade temporadas para el anime: {animeData?.name || 'Nuevo Anime'}
        </p>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h4 className="text-md font-medium text-gray-300">
          Temporadas ({seasons.length})
        </h4>
        <button
          type="button"
          onClick={addNewSeason}
          className="text-blue-400 hover:text-blue-300"
        >
          + Añadir temporada
        </button>
      </div>

      <div className="space-y-4">
        {seasons.map((season, index) => (
          <div 
            key={season.tempId} 
            className="p-4 border border-gray-600 rounded-lg bg-gray-800 flex justify-between items-center"
          >
            <div>
              <h5 className="font-medium text-white">
                {season.title}
              </h5>
              <p className="text-sm text-gray-300">
                {season.description.length > 50 
                  ? `${season.description.substring(0, 50)}...` 
                  : season.description || 'Sin descripción'}
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => editSeason(index)}
                className="text-blue-400 hover:text-blue-300"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => deleteSeason(index)}
                className="text-red-400 hover:text-red-300"
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}

        {seasons.length === 0 && (
          <div className="text-center p-6 border border-dashed border-gray-600 rounded-lg bg-gray-800 text-gray-400">
            No hay temporadas añadidas. Haz clic en "Añadir temporada".
          </div>
        )}
      </div>

      {editingIndex !== null && (
        <div className="mt-6 p-4 border border-gray-600 rounded-lg bg-gray-800">
          <h4 className="text-md font-medium mb-4 text-white">
            {editingIndex === seasons.length ? 'Añadir temporada' : `Editar ${seasons[editingIndex]?.title || ''}`}
          </h4>
          
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300">Número de temporada</label>
                <input
                  type="number"
                  value={seasonNumber}
                  onChange={(e) => setSeasonNumber(parseInt(e.target.value, 10))}
                  required
                  min="1"
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300">Título</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300">Descripción</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300">URL del póster</label>
                <input
                  type="text"
                  value={poster}
                  onChange={(e) => setPoster(e.target.value)}
                  placeholder="https://ejemplo.com/poster.jpg"
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300">Fecha de estreno</label>
                <input
                  type="date"
                  value={releaseDate}
                  onChange={(e) => setReleaseDate(e.target.value)}
                  required
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setEditingIndex(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                Guardar Temporada
              </button>
            </div>
          </form>
        </div>
      )}

      {seasons.length > 0 && (
        <div className="pt-4">
          <button
            type="button"
            onClick={handleComplete}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md"
          >
            Continuar
          </button>
        </div>
      )}
    </div>
  );
};

export default SeasonFormList;
